<template>
  <v-container>
    <!-- Header with Date Filters -->
    <v-row class="align-center mb-4">
      <!-- Left side: Title + Dates -->
      <v-col cols="6" class="d-flex align-center">
        <h2 class="mb-0 me-4">Portfolio List</h2>
        <v-text-field label="From Date" type="date" density="compact" v-model="fromDate" style="max-width: 160px"
          class="me-3" />
        <v-text-field label="To Date" type="date" density="compact" v-model="toDate" style="max-width: 160px"
          class="me-3" />
      </v-col>
  
      <!-- Right side: Buttons -->
      <v-col cols="6" class="d-flex justify-end align-center">
        <v-btn color="primary" class="me-2" @click="showCreateDialog = true">
          + New Portfolio
        </v-btn>
        <v-btn color="secondary" @click="showPastDialog = true">
          + Past Portfolio
        </v-btn>
      </v-col>
    </v-row>
    <!-- Total PnL Top Left -->
    <v-row class="mb-2" justify="start">
      <v-col cols="auto">
        <div class="d-flex align-center">
          <strong class="mr-2">Total PnL:</strong>
          <span :class="totalPnL >= 0 ? 'text-green' : 'text-red'">
            {{ formatCurrency(totalPnL.toFixed(2) ) }}
          </span>
        </div>
      </v-col>
    </v-row>
  
    <!-- Portfolio List Table with Server-Side Pagination -->
    <v-data-table-server v-model:items-per-page="itemsPerPage" :headers="portfolioHeaders" :items="portfolios"
      :items-length="totalItems" :loading="loading" :items-per-page-options="[10, 25, 50, 100]" class="elevation-1"
      @update:options="loadItems">
      <template #[`item.pnl`]="{ item }">
        <span :class="item.pnl >= 0 ? 'text-green' : 'text-red'">
          {{ formatCurrency(item.pnl) }}
        </span>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn size="small" color="primary" @click="viewPortfolio(item)">View</v-btn>
      </template>
    </v-data-table-server>
  
    <!-- Create Portfolio Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Create Portfolio</v-card-title>
        <v-card-text>
          <v-text-field label="Portfolio Name" v-model="newPortfolio.name" />
          <v-text-field label="Start Date" type="date" v-model="newPortfolio.startDate" />
  
          <h3 class="mt-4">Select Trades to Add</h3>
          <v-data-table show-select :headers="tradeHeaders" :items="currentTrades" item-value="tradeId"
            v-model="selectedTrades" class="elevation-1">
            <template #[`item.pnl`]="{ item }">
              <span :class="item.pnl >= 0 ? 'text-green' : 'text-red'">
                {{ formatCurrency(item.pnl) }}
              </span>
            </template>
          </v-data-table>
  
          <v-alert v-if="selectedTrades.length > 0" type="info" density="compact" class="mt-4">
            Selected {{ selectedTrades.length }} trade(s) with total P&L of {{ formatCurrency(calculateSelectedPnl()) }}
          </v-alert>
          <v-alert v-else type="warning" density="compact" class="mt-4">
            No trades selected. Please select at least one trade for your portfolio.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="savePortfolio" :disabled="selectedTrades.length === 0">Save Portfolio</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Past Portfolio Dialog -->
    <v-dialog v-model="showPastDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Create Past Portfolio</v-card-title>
        <v-card-text>
          <v-text-field label="Portfolio Name" v-model="newPortfolio.name" />
          <v-text-field label="Start Date" type="date" v-model="newPortfolio.startDate" />
          <v-text-field label="End Date" type="date" v-model="newPortfolio.endDate" />
  
          <h3 class="mt-4">Select Past Trades to Add</h3>
          <v-data-table show-select :headers="tradeHeaders" :items="pastTrades" item-value="tradeId"
            v-model="selectedPastTrades" class="elevation-1">
            <template #[`item.pnl`]="{ item }">
              <span :class="item.pnl >= 0 ? 'text-green' : 'text-red'">
                {{ formatCurrency(item.pnl) }}
              </span>
            </template>
          </v-data-table>
  
          <v-alert v-if="selectedPastTrades.length > 0" type="info" density="compact" class="mt-4">
            Selected {{ selectedPastTrades.length }} trade(s) with total P&L of
            {{ formatCurrency(calculatePastPnl()) }}
          </v-alert>
          <v-alert v-else type="warning" density="compact" class="mt-4">
            No past trades selected. Please select at least one trade.
          </v-alert>
        </v-card-text>
  
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closePastDialog">Cancel</v-btn>
          <v-btn color="secondary" @click="savePastPortfolio" :disabled="selectedPastTrades.length === 0">
            Save Past Portfolio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

const router = useRouter();

const store = useStore();

const showCreateDialog = ref(false);
const selectedTrades = ref([]);
const currentTrades = ref([]);
const showPastDialog = ref(false);
const pastTrades = ref([]);
const selectedPastTrades = ref([]);
// Reactive variables for table data and pagination
const portfolios = ref([]);
const loading = ref(true);
const totalItems = ref(0);
const itemsPerPage = ref(50);

// Calculate default dates
const today = new Date();
const sixMonthsAgo = new Date();
sixMonthsAgo.setMonth(today.getMonth() - 6);

// Reactive variables for date range with default values
const fromDate = ref('2025-07-31');
const toDate = ref(today.toISOString().substring(0, 10));

const portfolioHeaders = [
  { title: "Name", key: "portfolioName" },
  { title: "Start Date", key: "portfolioStart" },
  { title: "End Date", key: "portfolioEnd" },
  { title: "PnL", key: "pnl" },
  { title: "Actions", key: "actions", sortable: false },
];

const tradeHeaders = [
  { title: "Trade ID", key: "tradeId" },
  { title: "Trade Name", key: "name" },
  { title: "Type", key: "type" },
  { title: "PnL", key: "pnl" },
];
const totalPnL = computed(() =>
  portfolios.value.reduce((sum, portfolio) => sum + (parseFloat(portfolio.pnl) || 0), 0)
)

const newPortfolio = ref({
  name: "",
  startDate: new Date().toISOString().substring(0, 10),
});
// watch dialog open → fetch past trades
watch(showPastDialog, async (val) => {
  if (val) {
    // auto-generate name
    const today = new Date();
    newPortfolio.value.name = `PORTFOLIO_${today.toISOString().substring(0, 10)}`;

    // fetch past trades from store
    const trades = await store.dispatch("trades/getExitedFreeTradeDetailList");
    console.log('📦 get past free trades API response:', trades);
    const list = trades?.exitedFreeTradeDetailList || [];

    pastTrades.value = list.map((t) => ({
      tradeId: t.tradeId,
      name: t.strategyName,
      type: t.strategyType,
      pnl: t.pnl,
    }));
  }
});

function calculatePastPnl() {
  return selectedPastTrades.value.reduce((acc, t) => acc + t.pnl, 0);
}

function closePastDialog() {
  showPastDialog.value = false;
  selectedPastTrades.value = [];
}

async function savePastPortfolio() {
  try {
    const tradeIds = selectedPastTrades.value;
    await store.dispatch("portfolio/createAndAddPastPortfolio", {
      portfolioName: newPortfolio.value.name,
      portfolioStart: newPortfolio.value.startDate,
      portfolioEnd: newPortfolio.value.endDate,
      isActive: false,
      portfolioType: "BALANCED",
      tradeIdList: tradeIds,
    });

    await getPortfolioInfoList({ page: 1, itemsPerPage: itemsPerPage.value });
    closePastDialog();
  } catch (e) {
    console.error("❌ Save Past Portfolio failed:", e);
  }
}

// Function to fetch portfolio data from the backend with pagination and date filters
async function getPortfolioInfoList(options) {
  try {
    loading.value = true;
    const { page, itemsPerPage } = options;
    const offset = (page - 1) * itemsPerPage;

    const res = await store.dispatch('portfolio/getPortfolioInfoList', {
      fromDate: fromDate.value,
      toDate: toDate.value,
      offset: offset,
      count: itemsPerPage,
      portfolioType: 'BALANCED'
    });

    portfolios.value = res.portoflioInfoList;
    // CORRECTED: Provide a default value for totalItems to ensure it is a number.
    totalItems.value = res.totalCount || 0;

    return res;
  } catch (err) {
    console.error('getPortfolioInfoList failed', err);
    throw err;
  } finally {
    loading.value = false;
  }
}

// Handler for v-data-table-server pagination updates
async function loadItems({ page, itemsPerPage, sortBy }) {
  await getPortfolioInfoList({ page, itemsPerPage, sortBy });
}

// Initial data load on component mount
onMounted(async () => {
  await getPortfolioInfoList({ page: 1, itemsPerPage: itemsPerPage.value });
});

// Watch for changes in date inputs to re-fetch data
watch([fromDate, toDate], async () => {
  await getPortfolioInfoList({ page: 1, itemsPerPage: itemsPerPage.value });
});

// Watch dialog open → fetch trades
watch(showCreateDialog, async (val) => {
  if (val) {
    // auto-generate portfolio name
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = today.toLocaleString("en-US", { month: "short" }).toUpperCase();
    const year = String(today.getFullYear()).slice(-2);
    newPortfolio.value.name = `PORTFOLIO_${day}${month}${year}`;

    // fetch trades from store
    const trades = await store.dispatch("trades/getActiveTradeDetailList");

    currentTrades.value = trades.tradeDetailList.map((t) => ({
      tradeId: t.tradeId,
      name: t.strategyName,
      type: t.strategyType,
      pnl: t.pnl,
    }));

    console.log("✅ currentTrades loaded:", currentTrades.value);
  }
});

// Watch selected trades live
watch(selectedTrades, (val) => {
  const filteredTrades = val.filter(trade => trade !== undefined);

  console.log("🟡 selectedTrades (objects):", JSON.parse(JSON.stringify(filteredTrades)));
  console.log("📦 selectedTradeIds:", filteredTrades.map((t) => t.tradeId));
}, { deep: true });

// Format currency function
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

// Calculate selected PnL
function calculateSelectedPnl() {
  return selectedTrades.value
    .filter(trade => trade !== undefined)
    .reduce((acc, t) => acc + t.pnl, 0);
}

// Close dialog function
function closeDialog() {
  showCreateDialog.value = false;
  selectedTrades.value = [];
}

async function savePortfolio() {
  try {
    const tradeIds = selectedTrades.value;

    console.log("🚀 saving tradeIds:", tradeIds);

    await store.dispatch("portfolio/createAndAddPortfolio", {
      portfolioName: newPortfolio.value.name,
      portfolioStart: newPortfolio.value.startDate,
      isActive: true,
      tradeIdList: tradeIds,
      portfolioType: "BALANCED",
    });

    // Re-fetch the data after a successful save
    await getPortfolioInfoList({ page: 1, itemsPerPage: itemsPerPage.value });

    closeDialog();
    newPortfolio.value = {
      name: "",
      startDate: new Date().toISOString().substring(0, 10),
    };
  } catch (error) {
    console.error("❌ Create Portfolio API error:", error);
    throw error;
  }
}

function viewPortfolio(item) {
  // open detail screen/modal
  console.log("Viewing portfolio:", item);
  router.push({
    path: "/portfolio/portfolioDetail",
    query: { portfolioId: item.portfolioId },
  });
}

function deletePortfolio(item) {
  if (confirm(`Are you sure you want to delete portfolio "${item.name}"?`)) {
    portfolios.value = portfolios.value.filter((p) => p.name !== item.name);
  }
}
</script>
